import get from 'lodash/get';
import head from 'lodash/head';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import once from 'lodash/once';
import { fromJS } from 'immutable';

import {
  PAGES__ROLE_ERROR,
  PAGES__ROLE_FINALIZER,
  PAGES__ROLE_REDIRECTER,
  PAGES__ROLE_TERMINATION_FINALIZER,
} from '../constants';

import { selectCondition } from '../conditions/selectors.conditions';

import {
  selectElementsByParentId,
  selectElementsByPageId,
  selectElementRefs,
} from '../elements/selectors.elements';

import { bugsnagNotify, isStaticElementType, replaceUtmTemplates } from '../../util';

const notifyBugsnagOnce = once(bugsnagNotify);

const rawSelect = ({ pages }) => pages.toList();
const finalizer = { id: PAGES__ROLE_FINALIZER, $role: PAGES__ROLE_FINALIZER };
const terminationFinalizer = {
  id: PAGES__ROLE_TERMINATION_FINALIZER,
  $role: PAGES__ROLE_TERMINATION_FINALIZER,
};

const findElements = (elements) => !!elements.find(({ type }) => !isStaticElementType(type));

function enhacePage(state, page, result) {
  if (!page) {
    return null;
  }
  const pageId = page.get('id');
  const elements = selectElementsByParentId(state, pageId);
  if (!elements.length) {
    return null;
  }
  const condition = selectCondition(state, page.get('conditionId'));
  const header = elements.find((element) => element.type === 'HeaderElement');
  const description = elements.find((element) => element.type === 'DescriptionElement');
  const canvas = elements.find((element) => element.type === 'CanvasElement');
  const canvasElements = selectElementsByParentId(state, canvas.id);
  const hasInputElements = findElements(canvasElements);
  const nextButtonIsVisible =
    page.get('terminating') || page.get('redirect') ? hasInputElements : true;
  const $role = page.get('redirect') ? PAGES__ROLE_REDIRECTER : undefined;
  const redirectLinkWithUtms = replaceUtmTemplates(page.get('redirectLink'), result);
  return page.merge({
    canvas,
    canvasElements,
    condition,
    hasInputElements,
    description,
    elements,
    header,
    nextButtonIsVisible,
    $role,
    redirectLinkWithUtms,
  });
}

export const selectPages = (state) => rawSelect(state).toJS();

export function selectPage(state, pages, pageId, result) {
  const pageExists = pages.find((page) => page.id === pageId);
  if (!pageExists) return null;
  if ('$role' in pageExists) return pageExists;
  const page = state.pages.get(pageId);
  const enhacesPage = enhacePage(state, page, result);
  return enhacesPage && enhacesPage.toJS();
}

export function selectPageById(state, pageId) {
  const pageFound = state.pages.filter((page) => page.get('id') === pageId).first();
  return pageFound && pageFound.toJS();
}

export function selectClientPages(state, surveyClient, result, pageError) {
  if (pageError) return [{ id: PAGES__ROLE_ERROR, $role: PAGES__ROLE_ERROR, error: pageError }];
  if (isEmpty(result) || state.pages.size === 0) return [];
  if (!state.result.get('displayOrder')) {
    notifyBugsnagOnce(new Error('Result has no displayOrder'), {
      metadata: {
        resultId: result.id,
        state: result.state,
        surveyId: surveyClient.surveyId,
        version: surveyClient.version,
      },
    });
    return [{ id: PAGES__ROLE_ERROR, $role: PAGES__ROLE_ERROR }];
  }

  let surveyWasTerminated = false;
  const pages = state.result
    .get('displayOrder')
    .map((pageId, displayOrder) => {
      const page = state.pages.get(pageId);
      if (!page) {
        notifyBugsnagOnce(new Error('Out of sync data received'), {
          metadata: {
            pageId,
            resultId: result.id,
            resultVersion: state.result.get('version'),
            pagesVersion: surveyClient.pagesVersion,
            displayOrder: state.result.get('displayOrder').toArray(),
            statePages: state.pages.map((localPage) => localPage.get('id')).toArray(),
            surveyId: surveyClient.surveyId,
            version: surveyClient.version,
          },
        });
        return fromJS({
          id: PAGES__ROLE_ERROR,
          $role: PAGES__ROLE_ERROR,
          terminating: true,
        });
      }
      return page.merge({ displayOrder });
    })
    .filter((page) => {
      const conditionId = page.get('conditionId');
      if (!conditionId) return true;
      const condition = selectCondition(state, conditionId);
      return get(condition, 'value', false);
    })
    .takeUntil((page) => {
      const exterminator = surveyWasTerminated;
      if (!exterminator) surveyWasTerminated = page.get('terminating') || page.get('redirect');
      return exterminator;
    });
  const jsPages = pages.toJS();
  if (surveyWasTerminated) {
    const lastPage = pages.last();
    const enhacedPage = enhacePage(state, lastPage);
    if (enhacedPage) {
      const nextButtonIsVisible = enhacedPage.get('nextButtonIsVisible');
      if (nextButtonIsVisible) {
        jsPages.push(terminationFinalizer);
      }
    }
  } else {
    jsPages.push(finalizer);
  }
  return jsPages;
}

const selectPageIndex = (pages, pageId) => pages.findIndex((page) => page.id === pageId);

export const selectCurrentPageIndex = (pages, result) => {
  const index = pages.findIndex((page) => page.id === result.currentPageId);
  const lastIndex = Math.max(pages.length - 1, 0);
  const currentPageIndex = index === -1 ? lastIndex : index;
  return currentPageIndex;
};

export const selectFirstPageId = (pages) => get(pages, [0, 'id']);

export function selectNextPageId(pages, pageId) {
  const { id: firstPageId } = head(pages);
  if (!pageId) return firstPageId;
  const index = selectPageIndex(pages, pageId);
  const { id: lastPageId } = last(pages);
  if (pageId === lastPageId) return get(pages, [index - 1, 'id'], firstPageId);
  return get(pages, [index + 1, 'id'], lastPageId);
}

export function selectPrevPageId(pages, pageId) {
  const index = selectPageIndex(pages, pageId);
  return get(pages, [index - 1, 'id'], last(pages).id);
}

export const selectRefPagesByElementName = (state, elementName) =>
  selectElementRefs(state, elementName).map((element) => selectPageById(state, element.pageId));

export const selectPageRefs = (state, pageId) =>
  selectElementsByPageId(state, pageId)
    .map(({ name: elementName }) => ({
      elementName,
      pages: selectRefPagesByElementName(state, elementName),
    }))
    .filter((result) => result.pages.length !== 0);
